// あそぶ画面（ピースプレビュー）のスタートボタン（PreviewStartBtn）と同じ形のボタン。
// 全部丸い角・上から下へ濃くなる面・下に厚み・左に白い丸のアイコン。押すと厚みごと沈む。
// 色は upsilon のボタンの色（緑・オレンジ・紫・青・赤・グレー）を元に、面を上下で濃淡にしたもの
import { MouseEvent } from "react";

export enum PillColor {
  GREEN = "green",
  ORANGE = "orange",
  PURPLE = "purple",
  BLUE = "blue",
  RED = "red",
  GRAY = "gray",
  // 白い面に色の文字（つかいかた・いいね・ほうこく・くわしく など、目立たせすぎない操作）
  WHITE_BLUE = "white_blue",
  WHITE_RED = "white_red",
  WHITE_GREEN = "white_green",
}

// 以前の btn の色クラス（CardButton.className など、データ側で持っている値）をピルの色に対応づける
export const PILL_COLOR_BY_BTN_CLASS: Record<string, PillColor> = {
  "btn-primary": PillColor.GREEN,
  "btn-secondary": PillColor.ORANGE,
  "btn-purple": PillColor.PURPLE,
  "btn-blue": PillColor.BLUE,
  "btn-danger": PillColor.RED,
  "btn-gray": PillColor.GRAY,
};

export enum PillSize {
  MD = "md",
  SM = "sm",
}

// --pill-edge は下の厚みの色、--pill-glow は下に落ちる影の色
const COLOR_CLASS: Record<
  PillColor,
  { face: string; circle: string; glyph: string; label: string }
> = {
  [PillColor.GREEN]: {
    face: "bg-gradient-to-b from-[#52eeb3] to-[#1bbd80] [--pill-edge:#149666] [--pill-glow:rgba(20,150,102,0.35)]",
    circle: "bg-white",
    glyph: "text-[#1bbd80]",
    label: "text-white",
  },
  [PillColor.ORANGE]: {
    face: "bg-gradient-to-b from-[#ffbe6b] to-[#f58d1f] [--pill-edge:#c46a12] [--pill-glow:rgba(196,106,18,0.35)]",
    circle: "bg-white",
    glyph: "text-[#f58d1f]",
    label: "text-white",
  },
  [PillColor.PURPLE]: {
    face: "bg-gradient-to-b from-[#9b7fbd] to-[#6b4d8a] [--pill-edge:#4e3868] [--pill-glow:rgba(78,56,104,0.35)]",
    circle: "bg-white",
    glyph: "text-[#6b4d8a]",
    label: "text-white",
  },
  [PillColor.BLUE]: {
    face: "bg-gradient-to-b from-[#a8d2fb] to-[#6aaef0] [--pill-edge:#4a86c4] [--pill-glow:rgba(74,134,196,0.35)]",
    circle: "bg-white",
    glyph: "text-[#6aaef0]",
    label: "text-white",
  },
  [PillColor.RED]: {
    face: "bg-gradient-to-b from-[#ff8a85] to-[#e84e49] [--pill-edge:#b64643] [--pill-glow:rgba(182,70,67,0.35)]",
    circle: "bg-white",
    glyph: "text-[#e84e49]",
    label: "text-white",
  },
  [PillColor.GRAY]: {
    face: "bg-gradient-to-b from-[#c3cad2] to-[#9aa3ad] [--pill-edge:#737c87] [--pill-glow:rgba(115,124,135,0.3)]",
    circle: "bg-white",
    glyph: "text-[#9aa3ad]",
    label: "text-white",
  },
  [PillColor.WHITE_BLUE]: {
    face: "bg-white [--pill-edge:#d9e6f4] [--pill-glow:rgba(74,134,196,0.2)]",
    circle: "bg-[#e6f1fb]",
    glyph: "text-[#4c9df1]",
    label: "text-[#4c9df1]",
  },
  [PillColor.WHITE_GREEN]: {
    face: "bg-white [--pill-edge:#d3eee2] [--pill-glow:rgba(20,150,102,0.2)]",
    circle: "bg-[#e3f8ef]",
    glyph: "text-[#1bbd80]",
    label: "text-[#1bbd80]",
  },
  [PillColor.WHITE_RED]: {
    face: "bg-white [--pill-edge:#f4dcdb] [--pill-glow:rgba(182,70,67,0.2)]",
    circle: "bg-[#fdeceb]",
    glyph: "text-[#fc6560]",
    label: "text-[#fc6560]",
  },
};

// 同じ列に並べるボタンは、同じ大きさにそろえる（高さが揃わないため）
const SIZE_CLASS: Record<
  PillSize,
  {
    btn: string;
    pl: string;
    pr: string;
    prIconOnly: string;
    circle: string;
    glyph: string;
    label: string;
  }
> = {
  [PillSize.MD]: {
    btn: "h-16 gap-2 shadow-[0_5px_0_0_var(--pill-edge),0_10px_16px_var(--pill-glow)] active:translate-y-[5px]",
    pl: "pl-3",
    pr: "pr-6",
    prIconOnly: "pr-3",
    circle: "w-10 h-10",
    glyph: "type-md",
    label: "type-md",
  },
  [PillSize.SM]: {
    btn: "h-12 gap-2 shadow-[0_4px_0_0_var(--pill-edge),0_6px_10px_var(--pill-glow)] active:translate-y-[4px]",
    pl: "pl-2",
    pr: "pr-4",
    prIconOnly: "pr-2",
    circle: "w-8 h-8",
    glyph: "type-body",
    label: "type-body",
  },
};

export const BuilderPillButton = ({
  color,
  size,
  icon,
  iconFilled = true,
  label,
  ariaLabel,
  disabled = false,
  type = "button",
  onClick,
  className = "",
}: {
  color: PillColor;
  size: PillSize;
  icon: string;
  iconFilled?: boolean;
  /** 無ければアイコンだけの丸いボタン */
  label?: string | number;
  ariaLabel?: string;
  disabled?: boolean;
  /** フォームの送信ボタンにするときは submit（onClick は無くてよい） */
  type?: "button" | "submit";
  /** カードの中などで親のクリックを止めたいときは、受け取ったイベントで stopPropagation する */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}) => {
  const c = COLOR_CLASS[color];
  const s = SIZE_CLASS[size];
  const hasLabel = label !== undefined && label !== "";

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel ?? (hasLabel ? String(label) : icon)}
      className={`type-inherit-font flex-row-el items-center justify-center box-border shrink-0 rounded-full border-none cursor-pointer select-none transition-all duration-100 active:shadow-[0_0_0_0_var(--pill-edge),0_3px_8px_var(--pill-glow)] disabled:bg-none disabled:bg-[#e0e4e7] disabled:shadow-none disabled:cursor-default disabled:active:translate-y-0 ${c.face} ${s.btn} ${s.pl} ${hasLabel ? s.pr : s.prIconOnly} ${className}`}
    >
      <span
        className={`flex-col-center shrink-0 rounded-full shadow-[inset_0_-2px_3px_rgba(0,0,0,0.08)] ${s.circle} ${disabled ? "bg-white" : c.circle}`}
      >
        <span
          className={`material-symbols-outlined ${iconFilled ? "material-symbols-filled" : ""} ${s.glyph} ${disabled ? "text-[#9aa3ad]" : c.glyph}`}
        >
          {icon}
        </span>
      </span>
      {/* type-text は white-space: pre-line を持つので、! で上書きして 1 行にする（ボタンの文字は改行しない）。
          押せないときは灰色の面に白い文字だと読めないので、濃い灰色の文字にする */}
      {hasLabel && (
        <p
          className={`type-text ${s.label} !whitespace-nowrap ${
            disabled ? "text-[#8a929c]" : `drop-shadow-[0_2px_0_rgba(0,0,0,0.12)] ${c.label}`
          }`}
        >
          {label}
        </p>
      )}
    </button>
  );
};
